import { FunctionComponent, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import Loading from '@/components/Loading/Loading'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { useTranslation } from 'react-i18next'
import styles from './staffProfile.module.scss'
import Title from '@/components/_custom/Title/title'
import UserInput from '@/components/_custom/UserInput/userInput'
import { GET_ACCOUNT, SET_DATA_SHARING_CONSENT, UPDATE_ACCOUNT } from '@/modules/API/account/accountHandler'
import { SelectOptionsDeprecated } from '@/types/Select'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { Diplomas } from '@/types/Staff'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import ProfileAddress from '../profileAddress/profileAddress'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { ArrowForward } from '@mui/icons-material'
import DeleteIcon from '@mui/icons-material/Delete'
import DeactiveAccountDialog from '../deactiveAccount/deactiveAccountDialog.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'

interface staffProfileProps {
    signUp?: boolean
    signUpAutolink?: boolean
}

const staffProfile: FunctionComponent<staffProfileProps> = ({ signUp, signUpAutolink }: staffProfileProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [userId, setUserId] = useState<number>(0)

    const [email, setEmail] = useState<string>('')
    const [firstname, setFirstname] = useState<string>('')
    const [lastname, setLastname] = useState<string>('')
    const [phone, setPhone] = useState<string>('')
    const [address, setAddress] = useState<string>('')
    const [city, setCity] = useState<string>('')
    const [zip, setZip] = useState<string>('')
    const [country, setCountry] = useState<string>('')
    const [diploma, setDiploma] = useState<Diplomas>(Diplomas.no)

    const [dataSharingConsent, setDataSharingConsent] = useState<boolean>(false)

    const [isDeactiveAccountOpen, setDeactiveAccountOpen] = useState<boolean>(false)

    const { loading: loadingStaff, error: errorStaff, data: dataStaff, refetch: refetchStaff } = useQuery(GET_ACCOUNT)

    useEffect(() => {
        if (dataStaff) {
            setUserId(dataStaff.getAccount.userId)
            setEmail(dataStaff.getAccount.email)
            setFirstname(dataStaff.getAccount.firstname)
            setLastname(dataStaff.getAccount.lastname)
            setPhone(dataStaff.getAccount.phone)
            setAddress(dataStaff.getAccount.address)
            setCity(dataStaff.getAccount.city)
            setZip(dataStaff.getAccount.zip)
            setCountry(dataStaff.getAccount.country)
            setDiploma(dataStaff.getAccount.degree || Diplomas.no)
            setDataSharingConsent(dataStaff.getAccount.dataSharingConsent || false)
        }
    }, [dataStaff])

    const [updateAccount] = useMutation(UPDATE_ACCOUNT, {
        variables: {
            firstname: firstname,
            lastname: lastname,
            phone: phone,
            address: address,
            city: city,
            zip: zip,
            country: country,
            diploma: diploma
        },
        onCompleted: (): void => {
            if (signUp || signUpAutolink) {
                navigate('/calendar')
            } else {
                showNotificationSuccess(t('profileUpdated'))
            }
            refetchStaff()
        },
        onError: (): void => {
            showNotificationError(t('profileUpdatedError'))
        }
    })

    const [setDataSharingConsentMutation] = useMutation(SET_DATA_SHARING_CONSENT, {
        onError: (): void => {
            showNotificationError(t('profileUpdatedError'))
        }
    })

    function handleDataSharingConsent(value: boolean): void {
        setDataSharingConsent(value)
        setDataSharingConsentMutation({ variables: { consent: value } })
    }

    function backToHome(): void {
        navigate(-1)
    }

    const setDiplomaSelect: SelectOptionsDeprecated = getDiplomaSelectOptions(t)

    const checkDisabled: boolean =
        !firstname ||
        !lastname ||
        !phone ||
        !address ||
        !city ||
        !zip ||
        !country ||
        (dataStaff.getAccount.email === email &&
            dataStaff.getAccount.firstname === firstname &&
            dataStaff.getAccount.lastname === lastname &&
            dataStaff.getAccount.phone === phone &&
            dataStaff.getAccount.address === address &&
            dataStaff.getAccount.city === city &&
            dataStaff.getAccount.zip === zip &&
            dataStaff.getAccount.country === country &&
            dataStaff.getAccount.degree === diploma)

    function updateDiploma(diploma: string) {
        setDiploma(diploma as Diplomas)
    }

    return (
        <>
            {loadingStaff && <Loading overall />}
            <div className={styles.PagePosition}>
                <div className={styles.ProfilPlacement}>
                    <Checkbox
                        text={t('dataSharingConsent')}
                        checked={dataSharingConsent}
                        onChange={handleDataSharingConsent}
                    />
                </div>
                <div className={styles.ProfilPlacement}>
                    {dataStaff && (
                        <div className={styles.Form}>
                            <div className={styles.Title}>
                                <Title text={t('personalInformation')} />
                            </div>
                            <div className={styles.InputContainer}>
                                <div className={styles.Input}>
                                    <UserInput
                                        label={t('lastname')}
                                        value={lastname}
                                        setValue={setLastname}
                                        type='text'
                                        required
                                        fullWidth
                                        format={'upper'}
                                        className={styles.UserInput}
                                        unallowedCharacters={'num'}
                                    />
                                    <UserInput
                                        label={t('firstname')}
                                        value={firstname}
                                        setValue={setFirstname}
                                        type='text'
                                        required
                                        fullWidth
                                        format={'pascal'}
                                        className={styles.UserInput}
                                        unallowedCharacters={'num'}
                                    />
                                </div>
                                <div className={styles.Input}>
                                    <UserInput
                                        label={t('email')}
                                        value={email}
                                        setValue={setEmail}
                                        type='text'
                                        disabled
                                        required
                                        fullWidth
                                        className={styles.UserInput}
                                    />
                                    <UserInput
                                        label={t('phone')}
                                        value={phone}
                                        setValue={setPhone}
                                        type='text'
                                        verify={'phone'}
                                        required
                                        fullWidth
                                        format={'phone'}
                                        className={styles.UserInput}
                                    />
                                </div>
                                <div className={styles.Input}>
                                    <SelectUnique
                                        className={styles.DiplomaInput}
                                        selectValue={diploma}
                                        setSelectValue={updateDiploma}
                                        label={t('diploma')}
                                        selectOptions={setDiplomaSelect}
                                        fullWidth
                                    />
                                </div>
                            </div>
                            <div className={styles.Title}>
                                <Title text={t('address')} />
                            </div>
                            <div className={styles.InputContainer}>
                                <ProfileAddress
                                    signUp={signUp || signUpAutolink}
                                    address={address}
                                    setAddress={setAddress}
                                    city={city}
                                    setCity={setCity}
                                    zip={zip}
                                    setZip={setZip}
                                    country={country}
                                    setCountry={setCountry}
                                />
                            </div>
                        </div>
                    )}
                    {errorStaff && <ErrorMessage error={t('generalError')} />}
                </div>
                <div className={styles.ProfilPlacement}>
                    {!signUp && !signUpAutolink ? (
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon type={'return'} onClick={backToHome} className={styles.Button} />
                            <ButtonTextIcon
                                type={'update'}
                                onClick={updateAccount}
                                className={styles.Button}
                                disabled={checkDisabled}
                            />
                        </div>
                    ) : (
                        <ButtonTextIcon
                            type={'custom'}
                            customText={t('continue')}
                            customIcon={<ArrowForward />}
                            onClick={updateAccount}
                            className={styles.Button}
                            disabled={checkDisabled}
                        />
                    )}
                </div>
                {!signUp && !signUpAutolink && (
                    <div className={styles.ProfilPlacement}>
                        <ButtonTextIcon
                            type={'custom'}
                            customText={t('deleteMyAccount')}
                            className={styles.ButtonDeleteAccount}
                            customIcon={<DeleteIcon />}
                            onClick={() => setDeactiveAccountOpen(true)}
                        />
                    </div>
                )}
            </div>
            {isDeactiveAccountOpen && (
                <DeactiveAccountDialog
                    open={isDeactiveAccountOpen}
                    handleClose={() => setDeactiveAccountOpen(false)}
                    userId={userId}
                    isParent={false}
                />
            )}
        </>
    )
}

export default staffProfile

export function getDiplomaSelectOptions(t: (value: string) => string): SelectOptionsDeprecated {
    return [
        {
            options: [
                { value: Diplomas.bafa, name: t('bafa') },
                { value: Diplomas.aepe, name: t('aepe') },
                { value: Diplomas.deap, name: t('deap') },
                { value: Diplomas.deeje, name: t('deeje') },
                { value: Diplomas.atsem, name: t('atsem') },
                { value: Diplomas.dep, name: t('dep') },
                { value: Diplomas.dei, name: t('dei') },
                { value: Diplomas.no, name: t('withoutDiploma') },
                { value: Diplomas.other, name: t('others') }
            ]
        }
    ]
}
